(() => {
  if (customElements.get('percept-product-reviews')) return;

  const node = (tag, className, text) => {
    const element = document.createElement(tag);
    if (className) element.className = `percept-reviews__${className}`;
    if (text != null) element.textContent = text;
    return element;
  };
  const safeUrl = (value) => {
    try {
      const url = new URL(value);
      return url.protocol === 'https:' ? url.href : '';
    } catch (_) { return ''; }
  };
  const stars = (rating) => {
    const value = Math.max(0, Math.min(5, Number(rating) || 0));
    const element = node('span', 'stars');
    element.style.setProperty('--fill', `${value / 5 * 100}%`);
    element.setAttribute('role', 'img');
    element.setAttribute('aria-label', `${value} out of 5 stars`);
    return element;
  };

  class ProductReviews extends HTMLElement {
    connectedCallback() {
      if (this.initialized) {
        if (this.interrupted) { this.interrupted = false; this.load(); }
        return;
      }
      this.initialized = true;
      this.reviews = [];
      this.offset = 0;
      this.visibleCount = 3;
      this.sortBy = 'quality:desc';
      this.starFilter = null;
      this.reviewPageCache = new Map();
      this.list = this.querySelector('.percept-reviews__list');
      this.status = this.querySelector('.percept-reviews__status');
      this.summary = this.querySelector('.percept-reviews__summary');
      this.more = this.querySelector('.percept-reviews__more');
      this.less = this.querySelector('.percept-reviews__less');
      this.retry = this.querySelector('.percept-reviews__retry');
      this.dialog = this.querySelector('dialog');
      this.sortButtons = [...this.querySelectorAll('[data-sort]')];
      this.more.addEventListener('click', () => {
        this.visibleCount += 3;
        if (this.visibleCount <= this.reviews.length) this.renderList();
        else this.load();
      });
      this.less.addEventListener('click', () => {
        this.controller?.abort();
        this.controller = null;
        this.visibleCount = 3;
        this.renderList();
        this.list.focus({ preventScroll: true });
        if (this.getBoundingClientRect().top < -100) this.scrollIntoView({ block: 'start' });
      });
      this.retry.addEventListener('click', () => this.load());
      this.sortButtons.forEach(button => button.addEventListener('click', () => {
        if (this.sortBy === button.dataset.sort) return;
        this.sortBy = button.dataset.sort;
        this.sortButtons.forEach(option => option.setAttribute('aria-pressed', String(option === button)));
        this.reset();
      }));
      this.dialog.querySelector('.percept-reviews__close').addEventListener('click', () => this.closePhoto());
      this.dialog.addEventListener('cancel', event => { event.preventDefault(); this.closePhoto(); });
      this.dialog.addEventListener('click', event => {
        if (event.target === this.dialog) this.closePhoto();
      });
      this.dialog.addEventListener('close', () => {
        this.clearPhotoLoads();
        this.photoResizeObserver?.disconnect();
        this.dialog.classList.remove('is-closing');
        this.restoreScroll();
        this.photoTrigger?.focus({ preventScroll: true });
      });
      this.dialog.querySelector('.percept-reviews__previous').addEventListener('click', () => this.showPhoto(this.photoIndex - 1));
      this.dialog.querySelector('.percept-reviews__next').addEventListener('click', () => this.showPhoto(this.photoIndex + 1));
      this.dialog.addEventListener('keydown', event => {
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          this.showPhoto(this.photoIndex + (event.key === 'ArrowLeft' ? -1 : 1));
        }
      });
      const stage = this.dialog.querySelector('.percept-reviews__image-stage');
      stage.addEventListener('scroll', () => {
        if (!this.dialog.open || !stage.clientWidth) return;
        cancelAnimationFrame(this.photoScrollFrame);
        this.photoScrollFrame = requestAnimationFrame(() => {
          this.photoIndex = Math.max(0, Math.min(this.photos.length - 1, Math.round(stage.scrollLeft / stage.clientWidth)));
          this.updatePhotoControls();
        });
      }, { passive: true });
      this.load();
    }

    disconnectedCallback() {
      this.interrupted = this.list?.getAttribute('aria-busy') === 'true';
      this.controller?.abort();
      clearTimeout(this.reviewLoadingTimer);
      this.reviewSwapAnimation?.cancel();
      clearTimeout(this.closeTimer);
      this.clearPhotoLoads();
      this.photoResizeObserver?.disconnect();
      cancelAnimationFrame(this.photoScrollFrame);
      if (this.dialog?.open) this.dialog.close();
      this.restoreScroll();
    }

    restoreScroll() {
      if (this.scrollLock) {
        const { y, styles } = this.scrollLock;
        Object.assign(document.body.style, styles);
        delete this.scrollLock;
        window.scrollTo({ top: y, left: 0, behavior: 'instant' });
      }
    }

    reset() {
      this.reviews = [];
      this.offset = 0;
      this.visibleCount = 3;
      // Preserve the rendered results and their height until replacement data
      // arrives. A failed request must not erase reviews the shopper can read.
      this.replacingReviews = true;
      this.load();
    }

    setFilter(score) {
      this.starFilter = this.starFilter === score ? null : score;
      this.summary.querySelectorAll('[data-rating]').forEach(button => {
        button.setAttribute('aria-pressed', String(Number(button.dataset.rating) === this.starFilter));
      });
      this.summary.querySelector('[data-all-ratings]').setAttribute('aria-pressed', String(this.starFilter === null));
      this.reset();
    }

    async load() {
      this.controller?.abort();
      clearTimeout(this.reviewLoadingTimer);
      const controller = new AbortController();
      this.controller = controller;
      const timeout = setTimeout(() => controller.abort(), 15000);
      this.list.setAttribute('aria-busy', 'true');
      this.more.disabled = true;
      this.less.disabled = true;
      this.retry.hidden = true;
      this.list.classList.remove('is-updating');
      const hasVisibleReviews = this.list.childElementCount > 0;
      if (!hasVisibleReviews) this.status.textContent = 'Loading reviews…';
      this.reviewLoadingTimer = setTimeout(() => {
        if (this.controller !== controller) return;
        this.list.classList.add('is-updating');
        this.status.textContent = hasVisibleReviews ? 'Updating reviews…' : 'Loading reviews…';
      }, 250);
      try {
        if (!this.dataset.apiKey?.startsWith('pk_')) throw new Error('Missing public API key');
        // Fera applies its product groups before sorting and filtering. Never
        // replace this with all-store reviews or a filter on the exact product.
        const url = new URL(`https://cdn.fera.ai/api/v3/public/products/${encodeURIComponent(this.dataset.productId)}/reviews`);
        url.search = new URLSearchParams({
          api_key: this.dataset.apiKey, include_product: 'true',
          limit: '3', offset: String(this.offset), sort_by: this.sortBy,
        });
        if (this.starFilter !== null) url.searchParams.set('rating', String(this.starFilter));
        const cacheKey = url.href;
        const cached = this.reviewPageCache.get(cacheKey);
        let payload;
        if (cached && Date.now() - cached.savedAt < 300000) {
          payload = cached.payload;
        } else {
          const response = await fetch(url, { signal: controller.signal, credentials: 'omit' });
          if (!response.ok) throw new Error(`Fera returned ${response.status}`);
          payload = await response.json();
        }
        if (!Array.isArray(payload.data) || !Number.isFinite(Number(payload.meta?.total_count))) throw new Error('Invalid review response');
        if (!this.isConnected || this.controller !== controller) return;
        if (!cached || payload !== cached.payload) {
          if (this.reviewPageCache.size >= 60) this.reviewPageCache.delete(this.reviewPageCache.keys().next().value);
          this.reviewPageCache.set(cacheKey, { payload, savedAt: Date.now() });
        }
        if (!this.rating && this.starFilter === null) {
          let rating = payload.product?.rating || payload.rating;
          if (Number(rating?.count) !== Number(payload.meta.total_count)) {
            // Fera's group summary can lag behind its published review feed.
            // Never guess which star bucket contains the missing reviews.
            try { rating = await this.reconcileRating(url, payload.meta.total_count, controller.signal); }
            catch (_) { rating = null; }
          }
          if (!this.isConnected || this.controller !== controller) return;
          this.rating = rating;
          this.renderSummary();
        }
        this.total = Number(payload.meta.total_count);
        this.syncProductProof();
        const seen = new Set(this.reviews.map(review => review.id));
        payload.data.forEach(review => {
          if (!review.is_test && !seen.has(review.id)) { this.reviews.push(review); seen.add(review.id); }
        });
        this.offset += payload.data.length;
        this.exhausted = this.offset >= this.total || payload.data.length === 0;
        this.renderList();
      } catch (error) {
        if (!this.isConnected || this.controller !== controller) return;
        this.status.textContent = this.replacingReviews && hasVisibleReviews
          ? 'Could not update reviews. Previous results are still shown. Please try again.'
          : 'Reviews could not be loaded. Please try again.';
        this.retry.hidden = false;
        this.more.hidden = true;
      } finally {
        clearTimeout(timeout);
        if (this.controller === controller) {
          clearTimeout(this.reviewLoadingTimer);
          this.list.classList.remove('is-updating');
          this.list.setAttribute('aria-busy', 'false');
          this.more.disabled = this.less.disabled = Boolean(this.replacingReviews);
        }
      }
    }

    async reconcileRating(sourceUrl, total, signal) {
      const expected = Number(total);
      const url = new URL(sourceUrl);
      url.searchParams.delete('rating');
      url.searchParams.set('limit', '100');
      url.searchParams.set('sort_by', 'created_at:asc');
      const seen = new Set();
      const counts = [0, 0, 0, 0, 0];
      let offset = 0;
      while (offset < expected) {
        url.searchParams.set('offset', String(offset));
        const response = await fetch(url, { signal, credentials: 'omit' });
        if (!response.ok) throw new Error('Could not reconcile review counts');
        const page = await response.json();
        if (!Array.isArray(page.data) || !page.data.length || Number(page.meta?.total_count) !== expected) {
          throw new Error('Incomplete or changing review group');
        }
        for (const review of page.data) {
          const score = Number(review.rating);
          if (!review.id || seen.has(review.id) || review.is_test || !Number.isInteger(score) || score < 1 || score > 5) {
            throw new Error('Invalid review group');
          }
          seen.add(review.id);
          counts[score - 1]++;
        }
        offset += page.data.length;
      }
      if (seen.size !== expected) throw new Error('Incomplete review group');
      const sum = counts.reduce((value, count, index) => value + count * (index + 1), 0);
      return { count: expected, counts, average: expected ? Math.round(sum / expected * 10) / 10 : 0 };
    }

    syncProductProof() {
      // Use the full group's rating count, never the filtered/paginated total.
      const count = Number(this.rating?.count);
      if (!Number.isFinite(count) || count < 0) return;
      document.querySelectorAll('[data-product-review-proof]').forEach(link => {
        if (link.dataset.productReviewProof !== this.dataset.productId) return;
        const formatted = count.toLocaleString(document.documentElement.lang || 'en');
        link.querySelector('.micro-proof__count-number').textContent = formatted;
        link.setAttribute('aria-label', `Read ${formatted} product ${count === 1 ? 'review' : 'reviews'}`);
        link.setAttribute('href', `#${this.id}`);
      });
    }

    renderList() {
      this.reviewSwapAnimation?.cancel();
      const previousHeight = this.list.getBoundingClientRect().height;
      const swapping = this.replacingReviews && this.list.childElementCount > 0;
      const fragment = document.createDocumentFragment();
      this.reviews.slice(0, this.visibleCount).forEach(review => fragment.append(this.renderReview(review)));
      this.list.replaceChildren(fragment);
      this.replacingReviews = false;
      this.less.disabled = false;
      this.list.classList.remove('is-updating');
      if (swapping && this.list.animate && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        const nextHeight = this.list.getBoundingClientRect().height;
        this.reviewSwapAnimation = this.list.animate([
          { height: `${previousHeight}px`, opacity: 0.82, overflow: 'clip' },
          { height: `${nextHeight}px`, opacity: 1, overflow: 'clip' },
        ], { duration: 180, easing: 'ease-out' });
      }
      const shown = Math.min(this.visibleCount, this.reviews.length);
      this.more.hidden = shown >= this.total || (this.exhausted && shown >= this.reviews.length);
      this.more.disabled = false;
      this.more.textContent = `Show ${Math.min(3, Math.max(0, this.total - shown))} more reviews`;
      this.more.setAttribute('aria-expanded', String(shown > 3));
      this.less.hidden = shown <= 3;
      this.retry.hidden = true;
      this.list.setAttribute('aria-busy', 'false');
      this.querySelector('.percept-reviews__toolbar').hidden = !(this.rating?.count ?? this.total);
      const filterText = this.starFilter === null ? '' : `${this.starFilter}-star `;
      this.querySelector('.percept-reviews__count').textContent = `${this.total} ${filterText}${this.total === 1 ? 'review' : 'reviews'}`;
      this.status.textContent = this.total === 0
        ? (this.starFilter === null ? 'No reviews yet.' : `No ${this.starFilter}-star reviews. Choose another rating to see more.`)
        : `Showing ${shown} of ${this.total} ${filterText}reviews`;
    }

    renderSummary() {
      const rating = this.rating;
      this.summary.replaceChildren();
      this.summary.hidden = !rating?.count;
      if (!rating?.count) return;
      const overview = node('div', 'overview');
      overview.append(node('span', 'average', Number(rating.average).toFixed(1)), stars(rating.average), node('p', '', `Based on ${rating.count} reviews`));
      const all = node('button', 'all-ratings', 'All ratings');
      all.type = 'button';
      all.dataset.allRatings = '';
      all.setAttribute('aria-pressed', 'true');
      all.addEventListener('click', () => this.setFilter(null));
      overview.append(all);
      this.summary.append(overview);
      if (!Array.isArray(rating.counts) || rating.counts.length !== 5) return;
      const distribution = node('div', 'distribution');
      distribution.setAttribute('role', 'group');
      distribution.setAttribute('aria-label', 'Filter reviews by star rating');
      for (let score = 5; score >= 1; score--) {
        const count = Number(rating.counts[score - 1]) || 0;
        const row = node('button', 'bar');
        row.type = 'button';
        row.dataset.rating = score;
        row.setAttribute('aria-label', `Show ${score}-star reviews (${count})`);
        row.setAttribute('aria-pressed', 'false');
        row.addEventListener('click', () => this.setFilter(score));
        const track = node('span', 'bar-track');
        const fill = node('span', 'bar-fill');
        fill.style.width = `${Math.min(100, count / Math.max(1, Number(rating.count)) * 100)}%`;
        track.append(fill);
        row.append(node('span', '', `${score} ★`), track, node('span', '', count));
        distribution.append(row);
      }
      this.summary.append(distribution);
    }

    closePhoto() {
      if (!this.dialog.open || this.dialog.classList.contains('is-closing')) return;
      this.dialog.classList.add('is-closing');
      const delay = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 140;
      this.closeTimer = setTimeout(() => this.dialog.close(), delay);
    }

    clearPhotoLoads() {
      (this.photoTimers || []).forEach(clearTimeout);
      this.photoTimers = [];
    }

    updatePhotoControls() {
      this.dialog.querySelector('.percept-reviews__image-caption').textContent = `${this.photoIndex + 1} / ${this.photos.length}`;
      const previous = this.dialog.querySelector('.percept-reviews__previous');
      const next = this.dialog.querySelector('.percept-reviews__next');
      previous.hidden = next.hidden = this.photos.length < 2;
      previous.disabled = this.photoIndex === 0;
      next.disabled = this.photoIndex === this.photos.length - 1;
    }

    showPhoto(index) {
      if (!this.photos?.length || this.dialog.classList.contains('is-closing')) return;
      this.photoIndex = Math.max(0, Math.min(this.photos.length - 1, index));
      const stage = this.dialog.querySelector('.percept-reviews__image-stage');
      const opening = !this.dialog.open;
      if (opening) {
        this.clearPhotoLoads();
        stage.replaceChildren();
        this.dialog.querySelector('.percept-reviews__photo-product').textContent = this.photoProduct;
        this.photos.forEach((photo, number) => {
          const slide = node('div', 'photo-slide');
          slide.setAttribute('role', 'group');
          slide.setAttribute('aria-roledescription', 'slide');
          slide.setAttribute('aria-label', `Photo ${number + 1} of ${this.photos.length}`);
          const image = node('img', 'full-photo');
          image.alt = photo.alt;
          image.width = image.height = 1200;
          image.draggable = false;
          image.decoding = 'async';
          const status = node('p', 'photo-status', 'Loading photo…');
          status.setAttribute('role', 'status');
          const retry = node('button', 'photo-retry', 'Try again');
          retry.type = 'button';
          retry.hidden = true;
          let timer;
          const load = () => {
            clearTimeout(timer);
            status.textContent = 'Loading photo…';
            status.hidden = false;
            retry.hidden = true;
            image.classList.remove('is-ready');
            slide.setAttribute('aria-busy', 'true');
            const fail = () => {
              clearTimeout(timer);
              status.textContent = 'This photo could not be loaded.';
              retry.hidden = false;
              slide.setAttribute('aria-busy', 'false');
            };
            image.onload = () => {
              clearTimeout(timer);
              image.classList.add('is-ready');
              status.hidden = retry.hidden = true;
              slide.setAttribute('aria-busy', 'false');
            };
            image.onerror = fail;
            timer = setTimeout(fail, 15000);
            this.photoTimers.push(timer);
            image.src = photo.url;
          };
          retry.addEventListener('click', load);
          slide.append(image, status, retry);
          stage.append(slide);
          load();
        });
        const style = document.body.style;
        this.scrollLock = { y: window.scrollY, styles: { overflow: style.overflow, position: style.position, top: style.top, width: style.width } };
        Object.assign(style, { overflow: 'hidden', position: 'fixed', top: `-${window.scrollY}px`, width: '100%' });
        this.dialog.showModal();
        if (typeof ResizeObserver !== 'undefined') {
          this.photoResizeObserver?.disconnect();
          this.photoResizeObserver = new ResizeObserver(() => {
            stage.scrollTo({ left: this.photoIndex * stage.clientWidth, behavior: 'instant' });
          });
          this.photoResizeObserver.observe(stage);
        }
      }
      stage.scrollTo({ left: this.photoIndex * stage.clientWidth, behavior: opening || window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
      this.updatePhotoControls();
    }

    renderReview(review) {
      const article = node('article', 'review');
      article.dataset.reviewId = review.id;
      const header = node('div', 'review-header');
      header.append(stars(review.rating));
      const author = node('p', 'author');
      author.append(node('strong', '', review.customer?.display_name || 'Anonymous'));
      if (review.is_verified) {
        const source = review.channel_code || review.channel?.code;
        const sourceName = source === 'shop_app' ? 'Shop' : (!source ? 'Fera' : review.channel?.name || 'Fera');
        const badge = node('span', 'verified', `✓ Verified by ${sourceName}`);
        badge.title = `Verified buyer · ${sourceName}`;
        author.append(badge);
      }
      header.append(author);
      article.append(header);
      const content = node('div', 'review-content');
      const copy = node('div', 'review-copy');
      if (review.heading) copy.append(node('h3', '', review.heading));
      if (review.body) {
        const fullText = String(review.body);
        const compactText = fullText.replace(/\s+/g, ' ').trim();
        const body = node('p', 'body', fullText);
        copy.append(body);
        if (Array.from(compactText).length > 160) {
          this.expandedReviewBodies ||= new Set();
          let excerpt = Array.from(compactText).slice(0, 160).join('');
          const lastSpace = excerpt.lastIndexOf(' ');
          if (lastSpace > 100) excerpt = excerpt.slice(0, lastSpace);
          excerpt = excerpt.trimEnd() + '…';
          body.id = `${this.list.id}-body-${review.id}`;
          const toggle = node('button', 'read-more');
          toggle.type = 'button';
          toggle.setAttribute('aria-controls', body.id);
          const update = () => {
            const expanded = this.expandedReviewBodies.has(review.id);
            body.textContent = expanded ? fullText : excerpt;
            toggle.textContent = expanded ? 'Read less' : 'Read more';
            toggle.setAttribute('aria-expanded', String(expanded));
          };
          toggle.addEventListener('click', () => {
            if (this.expandedReviewBodies.has(review.id)) this.expandedReviewBodies.delete(review.id);
            else this.expandedReviewBodies.add(review.id);
            update();
          });
          update();
          copy.append(toggle);
        }
      }
      if (review.product?.name) {
        const product = node('p', 'product');
        product.append(node('span', 'product-label', 'Product: '), document.createTextNode(review.product.name));
        copy.append(product);
      }
      content.append(copy);
      const media = node('div', 'media');
      const photos = (review.media || []).filter(item => item.type === 'photo' && safeUrl(item.url));
      (review.media || []).forEach((item) => {
        const url = safeUrl(item.url);
        if (!url) return;
        if (item.type === 'photo') {
          const button = node('button', 'photo');
          button.type = 'button';
          button.setAttribute('aria-label', `View customer photo of ${review.product?.name || 'artwork'}`);
          const image = node('img');
          image.src = safeUrl(item.thumbnail_url) || url;
          image.alt = item.alt_text || `Customer photo of ${review.product?.name || 'artwork'}`;
          image.loading = 'lazy';
          image.width = 160;
          image.height = 160;
          button.append(image);
          button.addEventListener('click', () => {
            this.photoTrigger = button;
            this.photos = photos.map(photo => ({ url: safeUrl(photo.url), thumbnail: safeUrl(photo.thumbnail_url), alt: photo.alt_text || `Customer photo of ${review.product?.name || 'artwork'}` }));
            this.photoProduct = review.product?.name || 'Customer photo';
            this.showPhoto(photos.indexOf(item));
          });
          media.append(button);
        } else if (item.type === 'video') {
          const video = node('video');
          video.src = url;
          video.controls = true;
          video.preload = 'none';
          video.playsInline = true;
          video.setAttribute('aria-label', 'Customer video');
          if (safeUrl(item.thumbnail_url)) video.poster = safeUrl(item.thumbnail_url);
          media.append(video);
        }
      });
      if (media.childElementCount) content.append(media);
      article.append(content);
      if (review.store_reply?.body) {
        const reply = node('div', 'reply');
        reply.append(node('strong', '', 'Response from Percept Helsinki'), node('p', '', review.store_reply.body));
        article.append(reply);
      }
      return article;
    }
  }
  customElements.define('percept-product-reviews', ProductReviews);
})();
